import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ChevronLeft, Star, Shield, IndianRupee, MessageCircle, Share2, Heart, CreditCard as Edit2, Trash2, Loader, AlertCircle, Tag, Youtube } from 'lucide-react';
import { supabase, getCollectible, subscribeToCollectible, getYouTubeVideoId } from '../lib/supabase';
import type { CollectibleWithImages } from '../lib/supabase';
import DeliveryAddressModal from './DeliveryAddressModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import ShareModal from './ShareModal';
import YouTubeEmbed from './YouTubeEmbed';
import ImageLightbox from './ImageLightbox';
import { ProductJsonLd, BreadcrumbJsonLd } from './JsonLd';
import { getWhatsAppLink } from '../lib/utils';

function CollectibleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [collectible, setCollectible] = useState<CollectibleWithImages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  const isUnavailable = collectible?.status === 'sold' || collectible?.status === 'unavailable';

  useEffect(() => {
    let isSubscribed = true;
    let subscription: any;

    const loadCollectible = async () => {
      if (!slug) {
        setError('Invalid URL');
        setLoading(false);
        return;
      }

      try {
        const data = await getCollectible(slug);
        
        if (!data) {
          throw new Error('Collectible not found');
        }

        if (isSubscribed) {
          setCollectible(data);
          setError(null);
        }
      } catch (err) {
        if (isSubscribed) {
          console.error('Error loading collectible:', err);
          setError('Collectible not found');
          setCollectible(null);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    // Load initial data
    loadCollectible();

    // Subscribe to changes
    if (slug) {
      subscription = subscribeToCollectible(slug, loadCollectible);
    }

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsUserAdmin(false);
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        setIsUserAdmin(profile?.is_admin || false);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsUserAdmin(false);
      }
    };

    checkAdminStatus();

    return () => {
      isSubscribed = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [slug]);

  const handleWhatsApp = () => {
    if (!collectible || isUnavailable) return;
    const message = `Hi, I'm interested in your ${collectible.name} priced at ₹${collectible.price}`;
    window.open(getWhatsAppLink(message), '_blank');
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleDelete = async () => {
    if (!collectible || isDeleting || !isUserAdmin) return;
    setIsDeleting(true);

    try {
      // First, delete all delivery addresses associated with this collectible
      const { error: deliveryError } = await supabase
        .from('delivery_addresses')
        .delete()
        .eq('collectible_id', collectible.id);

      if (deliveryError) throw deliveryError;

      // Then delete all images from storage
      if (collectible.images && collectible.images.length > 0) {
        const imagePaths = collectible.images.map(img => {
          const url = new URL(img.image_url);
          return url.pathname.split('/').slice(-2).join('/');
        });

        for (const path of imagePaths) {
          await supabase.storage
            .from('collectible-images')
            .remove([path]);
        }
      }

      // Finally delete the collectible (this will cascade delete images from the database)
      const { error: deleteError } = await supabase
        .from('collectibles')
        .delete()
        .eq('id', collectible.id);

      if (deleteError) throw deleteError;

      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error deleting collectible:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete collectible');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDeliverySubmit = async (deliveryData: any) => {
    if (!collectible) return;

    try {
      const { error: addressError } = await supabase
        .from('delivery_addresses')
        .insert({
          ...deliveryData,
          collectible_id: collectible.id
        });

      if (addressError) throw addressError;

      const upiDetails = {
        pa: 'ramshivexports-1@okhdfcbank',
        pn: 'OldCoinage.Com',
        tn: `Payment for ${collectible.name}`,
        am: collectible.price.toString(),
        cu: 'INR'
      };

      const upiUrl = `upi://pay?pa=${upiDetails.pa}&pn=${encodeURIComponent(upiDetails.pn)}&tn=${encodeURIComponent(upiDetails.tn)}&am=${upiDetails.am}&cu=${upiDetails.cu}`;

      setShowDeliveryModal(false);
      window.location.href = upiUrl;
    } catch (err) {
      console.error('Error saving delivery address:', err);
      alert('Failed to save delivery address. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !collectible) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Item Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'This collectible does not exist.'}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium"
          >
            <ChevronLeft className="h-5 w-5" />
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const sections = [
    { title: 'Description', content: collectible.description },
    { title: 'Condition', content: collectible.description.match(/Condition:(.*?)(?=\n\n|$)/s)?.[1]?.trim() },
    { title: 'Authentication', content: collectible.description.match(/Authentication:(.*?)(?=\n\n|$)/s)?.[1]?.trim() },
    { title: 'Historical Context', content: collectible.description.match(/Historical Context:(.*?)(?=\n\n|$)/s)?.[1]?.trim() },
    { title: 'Specifications', content: collectible.description.match(/Specifications:(.*?)(?=\n\n|$)/s)?.[1]?.trim() },
    { title: 'Investment Potential', content: collectible.description.match(/Investment Potential:(.*?)(?=\n\n|$)/s)?.[1]?.trim() }
  ].filter(section => section.content);

  return (
    <>
      <Helmet>
        <title>{collectible.name} - OldCoinage.com</title>
        <meta name="description" content={collectible.description.slice(0, 160)} />
      </Helmet>
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://oldcoinage.com/' },
          { name: collectible.category?.name || 'Collectibles', url: 'https://oldcoinage.com/' },
          { name: collectible.name, url: `https://oldcoinage.com/${collectible.slug}` }
        ]}
      />
      <ProductJsonLd
        name={collectible.name}
        description={collectible.description}
        image={collectible.images?.map(img => img.image_url) || []}
        url={`https://oldcoinage.com/${collectible.slug}`}
        price={collectible.price}
        availability={collectible.status === 'available' ? 'InStock' : collectible.status === 'sold' ? 'SoldOut' : 'OutOfStock'}
        category={collectible.category?.name}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 group"
          >
            <ChevronLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            Back to Collections
          </button>

          {isUserAdmin && (
            <div className="flex items-center gap-3">
              <Link
                to={`/edit/${collectible.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Edit2 className="h-5 w-5" />
                Edit
              </Link>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                <Trash2 className="h-5 w-5" />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <div
              className="aspect-square bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl overflow-hidden relative cursor-zoom-in group shadow-soft"
              onClick={() => setShowLightbox(true)}
            >
              {collectible.images?.[selectedImageIndex] && (
                <img
                  src={collectible.images[selectedImageIndex].image_url}
                  alt={collectible.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity transform scale-90 group-hover:scale-100">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-soft-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
              {isUnavailable && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-red-500/95 backdrop-blur-sm text-white shadow-soft">
                    <Tag className="h-4 w-4" />
                    {collectible.status === 'sold' ? 'Sold' : 'Unavailable'}
                  </span>
                </div>
              )}
            </div>

            {collectible.images && collectible.images.length > 1 && (
              <div className="grid grid-cols-6 gap-2">
                {collectible.images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setSelectedImageIndex(index)}
                    onDoubleClick={() => {
                      setSelectedImageIndex(index);
                      setShowLightbox(true);
                    }}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                      selectedImageIndex === index
                        ? 'border-primary-500 ring-2 ring-primary-200 shadow-sm'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={image.image_url}
                      alt={`${collectible.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {collectible.youtube_url && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Youtube className="h-5 w-5 text-red-600" />
                  Video Presentation
                </h3>
                <YouTubeEmbed
                  videoId={getYouTubeVideoId(collectible.youtube_url)!}
                  title={collectible.name}
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3">
                  {collectible.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-gold-700 bg-gradient-to-r from-gold-100 to-gold-50 px-3 py-1.5 rounded-lg border border-gold-200/50">
                    <Star className="h-4 w-4 fill-gold-500" />
                    <span className="font-semibold">
                      {(4.7 + (Math.random() * 0.3)).toFixed(1)}
                    </span>
                  </span>
                  <span className="inline-flex items-center text-primary-700 bg-gradient-to-r from-primary-100 to-primary-50 px-3 py-1.5 rounded-lg text-sm font-medium border border-primary-200/50">
                    Limited Edition
                  </span>
                  {isUnavailable && (
                    <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 border border-red-200/50">
                      <Tag className="h-4 w-4" />
                      {collectible.status === 'sold' ? 'Sold' : 'Unavailable'}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleShare}
                className="p-2.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
              >
                <Share2 className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-end gap-4 mb-8 p-5 bg-gradient-to-br from-gold-50 to-white rounded-2xl border border-gold-100">
              <div>
                <p className="text-sm text-gray-500 mb-1">Price</p>
                <p className="text-4xl font-display font-bold text-gray-900">
                  ₹{collectible.price.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                  <Shield className="h-3.5 w-3.5 text-primary-500" />
                  Authentication Included
                </p>
              </div>
              <button className="ml-auto p-2.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                <Heart className="h-6 w-6" />
              </button>
            </div>

            <div className="flex gap-4 mb-8">
              <button
                onClick={handleWhatsApp}
                disabled={isUnavailable}
                className="flex-1 premium-button-primary disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
              >
                <MessageCircle className="h-5 w-5" />
                Chat on WhatsApp
              </button>
              <button
                onClick={() => setShowDeliveryModal(true)}
                disabled={isUnavailable}
                className="flex-1 premium-button-gold disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none"
              >
                <IndianRupee className="h-5 w-5" />
                Purchase Now
              </button>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-2xl p-5 flex items-start gap-4 mb-8">
              <div className="p-2.5 bg-primary-100 rounded-xl">
                <Shield className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Authenticity Guaranteed</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Every item is verified by our expert numismatists and comes with a certificate of authenticity.
                </p>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {sections.map((section, index) => (
                <div key={index} className="py-6 first:pt-0 last:pb-0">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                  <div className="prose prose-gray prose-sm">
                    {section.content?.split('\n').map((paragraph, i) => (
                      <p key={i} className="mb-2 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showDeliveryModal && (
          <DeliveryAddressModal
            isOpen={showDeliveryModal}
            onClose={() => setShowDeliveryModal(false)}
            onSubmit={handleDeliverySubmit}
            collectible={collectible}
          />
        )}

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title={collectible.name}
        />

        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={collectible.name}
          description={collectible.description}
          imageUrl={collectible.images?.[0]?.image_url}
          price={collectible.price}
          url={window.location.href}
        />

        <ImageLightbox
          images={collectible.images || []}
          initialIndex={selectedImageIndex}
          isOpen={showLightbox}
          onClose={() => setShowLightbox(false)}
          productName={collectible.name}
        />
      </div>
    </>
  );
}

export default CollectibleDetail;